/**
 * Supabase Service Client
 *
 * Creates a Supabase client with service role credentials
 * to bypass Row Level Security (RLS) for admin operations.
 */

import { createClient, type SupabaseClient } from '@supabase/supabase-js'
import type { Database } from '@/supabase/types'

export const supabaseNoStoreFetch: typeof fetch = (input, init) =>
  fetch(input, {
    ...init,
    cache: 'no-store'
  })

let tlsDispatcher: unknown | null = null

function getServiceFetch(): typeof fetch {
  // In Node.js, auth.losi.one uses a self-signed certificate.
  // Use undici Agent to allow it without disabling TLS globally.
  // eval('require') prevents webpack from bundling undici for Edge runtime.
  if (typeof window === 'undefined' && !tlsDispatcher && typeof process !== 'undefined' && process.versions?.node) {
    try {
      // eslint-disable-next-line no-eval
      const { Agent } = eval("require")('undici') as { Agent: new (opts: any) => unknown }
      tlsDispatcher = new Agent({ connect: { rejectUnauthorized: false } })
    } catch {
      // undici not available (Edge runtime) — use default fetch
    }
  }

  return (input: RequestInfo | URL, init?: RequestInit) =>
    fetch(input, {
      ...init,
      cache: 'no-store',
      ...(tlsDispatcher ? { dispatcher: tlsDispatcher } : {})
    } as any)
}

/**
 * Creates a Supabase client with service role credentials
 * to bypass Row Level Security (RLS) for admin operations.
 *
 * Server-only. Prefer getDefaultClient() helpers in db/* that fall back to the
 * browser client on the client.
 */
export function createServiceClient(): SupabaseClient<Database> {
  if (typeof window !== 'undefined') {
    throw new Error('createServiceClient is server-only')
  }

  const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL
  const supabaseServiceKey = process.env.SUPABASE_SERVICE_ROLE_KEY

  if (!supabaseUrl || !supabaseServiceKey) {
    throw new Error('Missing Supabase service credentials')
  }

  return createClient<Database>(supabaseUrl, supabaseServiceKey, {
    auth: {
      autoRefreshToken: false,
      persistSession: false
    },
    global: {
      fetch: getServiceFetch()
    }
  }) as SupabaseClient<Database>
}
